import type { ComponentProps } from 'react'
import Sheet from 'components/utils/Sheet'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { UspTileSize } from 'components/Common/UspTile/UspTile'
import { rem } from 'polished'
import styled from 'styled-components'
import UspsBase from 'components/Common/UspsBase/UspsBase'

/** Global Kit USPs slot: 40px padding on the surface; the page container owns width. */
const CARD_PADDING = 40 as const
const CARD_RADIUS = '2XL' as const

const PageSlot = styled(LayoutContainer)`
  padding-block: ${rem(40)};
`

const Surface = styled(Sheet)`
  width: 100%;
`

type SheetBackground = NonNullable<ComponentProps<typeof Sheet>['background']>
type UspsBackground = Exclude<SheetBackground, 'light-background-alt'>
type UspsBaseProps = ComponentProps<typeof UspsBase>

interface Props extends Utils.PropsWithDataAttributes, Pick<UspsBaseProps,
  'heading' | 'children' | 'primaryActionButtonProps' | 'secondaryActionButtonProps'
> {
  /**
   * Fill for the contained USPs card. Defaults to no background so the section sits on the page.
   * Light Background ALT is not supported on this module.
   *
   * @default 'none'
   */
  background?: UspsBackground
  /**
   * Padding inside the USPs card. Same format as CSS padding.
   *
   * @default 40
   */
  padding?: ComponentProps<typeof Sheet>['padding']
  /**
   * Locks large or small layout. When omitted, CSS switches at the desktop breakpoint
   * so the first paint and hydration use the same attributes.
   */
  size?: UspTileSize
  className?: string
}

function Usps({
  background = 'none',
  padding = CARD_PADDING,
  className,
  size,
  heading,
  children,
  primaryActionButtonProps,
  secondaryActionButtonProps,
  ...rest
}: Props) {
  return <PageSlot className={className}>
    <Surface
      {...rest}
      background={background}
      padding={padding}
      borderRadius={CARD_RADIUS}
    >
      <UspsBase
        size={size}
        heading={heading}
        primaryActionButtonProps={primaryActionButtonProps}
        secondaryActionButtonProps={secondaryActionButtonProps}
      >
        {children}
      </UspsBase>
    </Surface>
  </PageSlot>
}

export default Usps
